import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import * as d3 from 'd3';
import { useCalcAmountDisplayMode } from './calc_components/useCalcAmountDisplayMode';
import { roundForDisplay } from './calc_components/calculatorFormatters';
import './HomeLoanAffordabilityCharts.css';

const MARGIN = { top: 36, right: 16, bottom: 48, left: 56 };

function formatLakh(value) {
  const n = Number(value) || 0;
  const lakhs = n / 100000;
  if (Math.abs(lakhs) >= 100) return `₹${d3.format(',.0f')(lakhs)}L`;
  if (Math.abs(lakhs) >= 10) return `₹${d3.format('.1f')(lakhs)}L`;
  return `₹${d3.format('.2f')(lakhs)}L`;
}

/** Tooltip / EMI label money: precise → full INR; rounded → Lakh after magnitude round. */
function formatChartMoney(value, mode, formatInr) {
  if (mode === 'precise') return formatInr(value);
  return formatLakh(roundForDisplay(value));
}

function useChartSize(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setSize({
        width: Math.max(0, Math.floor(rect.width)),
        height: Math.max(0, Math.floor(rect.height)),
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return size;
}

function buildStackRows(years) {
  return (years || []).map((row) => {
    const income = Math.max(0, Number(row.income) || 0);
    const expenses = Math.max(0, Number(row.expenses) || 0);
    const rawSurplus = Number(row.surplus) || 0;
    const surplus = Math.max(0, Math.min(income, rawSurplus));
    const expenseFill = Math.max(0, income - surplus);
    const annualEmi = Math.max(0, Number(row.annualEmi) || 0);
    return {
      year: row.year,
      income,
      expenses,
      surplus,
      expenseFill,
      annualEmi,
      deficit: rawSurplus < -0.5,
    };
  });
}

/** Comfortable = surplus stays at least 25% of annual EMI above the EMI line. */
const COMFORTABLE_BUFFER_RATIO = 0.25;

/**
 * Chart callout based on surplus vs annual EMI over the projection.
 * @returns {{ tone: 'stress'|'ok'|'comfortable', text: string } | null}
 */
export function getSurplusEmiOutlook(years) {
  const rows = Array.isArray(years) ? years : [];
  if (!rows.length) return null;

  const firstStress = rows.find((row) => {
    const surplus = Number(row.surplus) || 0;
    const annualEmi = Number(row.annualEmi) || 0;
    return surplus + 0.5 < annualEmi;
  });

  if (firstStress) {
    return {
      tone: 'stress',
      text: `In the year ${firstStress.year}, your EMI will exceed your surplus.`,
    };
  }

  const annualEmi = Math.max(0, Number(rows[0].annualEmi) || 0);
  let minBuffer = Infinity;
  for (const row of rows) {
    const surplus = Number(row.surplus) || 0;
    const emi = Number(row.annualEmi) || 0;
    minBuffer = Math.min(minBuffer, surplus - emi);
  }

  if (
    annualEmi <= 0 ||
    (Number.isFinite(minBuffer) && minBuffer >= annualEmi * COMFORTABLE_BUFFER_RATIO)
  ) {
    return {
      tone: 'comfortable',
      text: 'Based on your inputs, you will have no difficulty paying your EMI during the first 10 years.',
    };
  }

  return {
    tone: 'ok',
    text: 'Based on your inputs, you should be able to pay your EMI during the first 10 years.',
  };
}

/**
 * Stacked columns: surplus (solid water) at bottom, expenses (light empty air) on top.
 * Column height = take-home income for that year. Horizontal line = total annual EMI.
 */
function drawSurplusWaterChart(svgEl, years, width, height, formatMoney) {
  d3.select(svgEl).selectAll('*').remove();
  const rows = buildStackRows(years);
  if (!rows.length || width < 80 || height < 80) return;
  const money = typeof formatMoney === 'function' ? formatMoney : formatLakh;

  const innerW = width - MARGIN.left - MARGIN.right;
  const innerH = height - MARGIN.top - MARGIN.bottom;
  const svg = d3.select(svgEl).attr('viewBox', `0 0 ${width} ${height}`);

  const defs = svg.append('defs');
  const waterGrad = defs
    .append('linearGradient')
    .attr('id', 'hla-water-fill')
    .attr('x1', '0%')
    .attr('x2', '0%')
    .attr('y1', '100%')
    .attr('y2', '0%');
  waterGrad.append('stop').attr('offset', '0%').attr('stop-color', '#0369a1');
  waterGrad.append('stop').attr('offset', '55%').attr('stop-color', '#0284c7');
  waterGrad.append('stop').attr('offset', '100%').attr('stop-color', '#0ea5e9');

  const emptyGrad = defs
    .append('linearGradient')
    .attr('id', 'hla-empty-fill')
    .attr('x1', '0%')
    .attr('x2', '0%')
    .attr('y1', '0%')
    .attr('y2', '100%');
  emptyGrad.append('stop').attr('offset', '0%').attr('stop-color', '#e0f2fe').attr('stop-opacity', 0.35);
  emptyGrad.append('stop').attr('offset', '100%').attr('stop-color', '#bae6fd').attr('stop-opacity', 0.55);

  const g = svg
    .append('g')
    .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

  const x = d3
    .scaleBand()
    .domain(rows.map((d) => d.year))
    .range([0, innerW])
    .paddingInner(0.28)
    .paddingOuter(0.12);

  const annualEmi = rows[0]?.annualEmi || 0;
  const yMax = Math.max(d3.max(rows, (d) => d.income) || 0, annualEmi);
  const y = d3
    .scaleLinear()
    .domain([0, yMax * 1.08 || 1])
    .nice()
    .range([innerH, 0]);

  g.append('g')
    .attr('class', 'hla-chart__axis hla-chart__axis--x')
    .attr('transform', `translate(0,${innerH})`)
    .call(
      d3
        .axisBottom(x)
        .tickFormat((d) => `Y${d}`)
        .tickSizeOuter(0),
    );

  g.append('g')
    .attr('class', 'hla-chart__axis hla-chart__axis--y')
    .call(d3.axisLeft(y).ticks(5).tickFormat(formatLakh).tickSizeOuter(0));

  g.append('text')
    .attr('class', 'hla-chart__axis-label')
    .attr('x', innerW / 2)
    .attr('y', innerH + 38)
    .attr('text-anchor', 'middle')
    .text('Year');

  const col = g
    .selectAll('.hla-water-col')
    .data(rows)
    .join('g')
    .attr('class', 'hla-water-col')
    .attr('transform', (d) => `translate(${x(d.year)},0)`);

  const bw = x.bandwidth();
  const radius = Math.min(8, bw / 2);

  // Glass outline (full income container)
  col
    .append('rect')
    .attr('class', 'hla-water-col__glass')
    .attr('x', 0)
    .attr('y', (d) => y(d.income))
    .attr('width', bw)
    .attr('height', (d) => Math.max(0, innerH - y(d.income)))
    .attr('rx', radius)
    .attr('ry', radius);

  // Surplus water (bottom)
  col
    .append('rect')
    .attr('class', 'hla-water-col__surplus')
    .attr('x', 1.5)
    .attr('y', (d) => y(d.surplus))
    .attr('width', Math.max(0, bw - 3))
    .attr('height', (d) => Math.max(0, innerH - y(d.surplus)))
    .attr('rx', Math.max(2, radius - 2))
    .attr('ry', Math.max(2, radius - 2));

  // Expense emptiness (top of container)
  col
    .append('rect')
    .attr('class', 'hla-water-col__expenses')
    .attr('x', 1.5)
    .attr('y', (d) => y(d.income))
    .attr('width', Math.max(0, bw - 3))
    .attr('height', (d) => Math.max(0, y(d.surplus) - y(d.income)))
    .attr('rx', Math.max(2, radius - 2))
    .attr('ry', Math.max(2, radius - 2));

  // Water surface line
  col
    .filter((d) => d.surplus > 0 && d.expenseFill > 0)
    .append('line')
    .attr('class', 'hla-water-col__surface')
    .attr('x1', 2)
    .attr('x2', bw - 2)
    .attr('y1', (d) => y(d.surplus))
    .attr('y2', (d) => y(d.surplus));

  // Annual EMI reference line
  if (annualEmi > 0) {
    const emiY = y(annualEmi);
    g.append('line')
      .attr('class', 'hla-emi-line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', emiY)
      .attr('y2', emiY);

    g.append('text')
      .attr('class', 'hla-emi-line__label')
      .attr('x', innerW - 2)
      .attr('y', emiY - 6)
      .attr('text-anchor', 'end')
      .text(`Annual EMI ${money(annualEmi)}`);
  }

  // Hover
  const tip = g
    .append('text')
    .attr('class', 'hla-chart__focus-tip')
    .attr('text-anchor', 'middle')
    .style('display', 'none');

  col
    .append('rect')
    .attr('class', 'hla-chart__overlay')
    .attr('x', 0)
    .attr('y', 0)
    .attr('width', bw)
    .attr('height', innerH)
    .on('mouseenter', function onEnter(_event, d) {
      tip
        .style('display', null)
        .attr('x', x(d.year) + bw / 2)
        .attr('y', Math.max(12, y(d.income) - 10))
        .text(
          `Y${d.year}: surplus ${money(d.surplus)} · expenses ${money(d.expenses)} · EMI ${money(d.annualEmi)}`,
        );
    })
    .on('mouseleave', () => tip.style('display', 'none'));
}

export default function SurplusWaterChart({ years, title = 'Surplus income & expenses' }) {
  const plotRef = useRef(null);
  const svgRef = useRef(null);
  const uid = useId().replace(/:/g, '');
  const { width, height } = useChartSize(plotRef);
  const { mode, formatInr } = useCalcAmountDisplayMode();
  const outlook = getSurplusEmiOutlook(years);
  const formatMoney = useCallback(
    (value) => formatChartMoney(value, mode, formatInr),
    [mode, formatInr],
  );

  useEffect(() => {
    if (!svgRef.current) return;
    drawSurplusWaterChart(
      svgRef.current,
      Array.isArray(years) ? years : [],
      width,
      height,
      formatMoney,
    );
  }, [years, width, height, formatMoney]);

  return (
    <figure className="hla-chart hla-chart--water" aria-label={title}>
      <figcaption className="hla-chart__title">{title}</figcaption>
      {outlook ? (
        <p
          className={`hla-chart__outlook hla-chart__outlook--${outlook.tone}`}
          role="status"
        >
          {outlook.text}
        </p>
      ) : null}
      <div className="hla-chart__legend" aria-hidden="true">
        <span className="hla-chart__legend-item hla-chart__legend-item--water">
          <i /> Surplus
        </span>
        <span className="hla-chart__legend-item hla-chart__legend-item--empty">
          <i /> Expenses
        </span>
        <span className="hla-chart__legend-item hla-chart__legend-item--emi">
          <i /> Annual EMI
        </span>
      </div>
      <div className="hla-chart__plot hla-chart__plot--water" ref={plotRef}>
        <svg
          ref={svgRef}
          className="hla-chart__svg"
          role="img"
          aria-labelledby={`hla-water-title-${uid}`}
        >
          <title id={`hla-water-title-${uid}`}>{title}</title>
        </svg>
      </div>
    </figure>
  );
}
